import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  TablePagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { useTranslation } from 'react-i18next';
import { organizationService } from '@services/organizationService';
import { contextService, type Context as TrackedContext } from '@services/contextService';
import { useActiveOrgStore } from '@store/index';
import {
  findingsService,
  type FindingStatus,
  type GroupedOrgFinding,
  type OrgFindingsFilters,
  type OrgFindingsFacets,
  FINDING_STATUSES,
  ALL_FINDING_STATUSES,
} from '@services/findingsService';
import type { Severity } from '@services/reportingService';
import type { Finding, FindingSeverity, FindingSource } from '@services/auditService';
import { parseBlobErrorBody, toDisplayError } from '../utils/apiError';
import FindingsTable from '@components/findings/FindingsTable';
import GroupedFindingsTable from '@components/findings/GroupedFindingsTable';
import BulkTriageBar from '@components/findings/BulkTriageBar';
import FindingsTrendPanel from '@components/findings/FindingsTrendPanel';
import { useBulkTriage } from '@components/findings/useBulkTriage';
import { canTriageFindings } from '@utils/findingTriage';
import { visuallyHidden } from '@mui/utils';

/**
 * REV-489: the "All …" choice in every filter used to be a MenuItem with an
 * empty value. MUI renders a Select whose value is `''` as a blank field, so
 * picking "All severities" emptied the control and the filter read as broken.
 * Each filter now uses an explicit sentinel — a real, displayable option —
 * which is mapped back to `undefined` when the query is built.
 */
const ALL = 'all';

type SourceFilter = FindingSource | typeof ALL;
type SeverityFilter = FindingSeverity | typeof ALL;
type StatusFilter = FindingStatus | typeof ALL;

/**
 * REV-489: the contexts endpoint caps `limit` at 100 (revenant-api
 * routes/organizations.js). This asked for 200, so every request 400'd and the
 * Tracked place filter was permanently empty. An org with more than 100 tracked
 * places still only sees the first 100 here — that needs an autocomplete rather
 * than a select, tracked separately.
 */
const CONTEXT_FILTER_LIMIT = 100;

/**
 * REV-512: the findings list is reachable from several places, and the entry
 * path carries intent — arriving from a tracked place means "this place's
 * findings". That intent was being dropped: TestRunDetailPage linked to
 * ?context_id=<id> and this page never read search params, so you landed on
 * every finding in the org with "All places" selected. Same data, different
 * result depending on how you got there.
 *
 * Values are validated against the same sets the controls offer. An unknown
 * value falls back to ALL rather than being forwarded — the API rejects an
 * out-of-range severity or a non-UUID context_id with a 400, which would blank
 * the whole list instead of just ignoring a bad link.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** REV-1097: a group's signature, as revenant-api's findingGroups.js makes it. */
const SIGNATURE_RE = /^[0-9a-f]{32}$/;

function paramFromAllowed<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: readonly T[]
): T | typeof ALL {
  const raw = params.get(key);
  return raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : ALL;
}

const SOURCES: FindingSource[] = ['auto', 'manual'];
const SEVERITIES: FindingSeverity[] = ['critical', 'major', 'minor', 'info'];
// REV-720: one list, in the service, so a scan-run view asking for "every
// status" cannot spell the vocabulary differently from the page that offers it.
const STATUSES = FINDING_STATUSES;

/**
 * REV-548. Closed set, so it can be validated here — unlike WCAG SC and
 * category, which come from per-org facets.
 *
 * 'unspecified' is a real value, not a sentinel: it is the report's own bucket
 * for a rule that declares no level, and the API maps it to IS NULL.
 */
const WCAG_LEVELS = ['A', 'AA', 'AAA', 'unspecified'] as const;
type WcagLevel = (typeof WCAG_LEVELS)[number];

/**
 * The status set the issues-breakdown report counts. Offered as ONE selectable
 * value rather than a multi-select: it is the only combination anything links
 * to, and a report row saying "AA: 12" must land on 12 — the plain default
 * ("anything but resolved") also counts false_positive and wont_fix.
 */
const UNRESOLVED = 'open,acknowledged,in_progress';
/**
 * REV-481: every status, including resolved. The plain default is "anything but
 * resolved", so the dashboard's "All findings recorded" card — which counts
 * every status — had no filter that could reproduce its own number.
 */
const ALL_STATUSES = ALL_FINDING_STATUSES;
const STATUS_VALUES = [UNRESOLVED, ALL_STATUSES, ...STATUSES];

/**
 * REV-133: org-wide findings list. Consultants, developers, and POs all
 * need a cross-audit cross-context view of open accessibility issues —
 * without having to pick an audit first.
 */
export default function OrgFindingsPage() {
  const { t } = useTranslation();
  // REV-456: Findings is a global nav surface. When reached via the global
  // /findings route there is no :slug, so the org is chosen on the page from a
  // picker. When reached via an org-scoped link (/orgs/:slug/findings) the org
  // is fixed by the URL and the picker is hidden.
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { activeOrg } = useActiveOrgStore();

  // REV-589: on the global /findings route the organization is whichever one
  // the header switcher holds — the page no longer carries a competing picker.
  // An org-scoped URL (/orgs/:slug/findings) still wins, so a deep link keeps
  // working regardless of what the header has selected.
  const effectiveSlug = routeSlug ?? activeOrg?.slug ?? '';

  const [orgId, setOrgId] = useState<string | null>(null);
  // REV-949: the caller's role in the organization decides whether bulk triage
  // is offered. Null until the organization has loaded (offers none).
  const [orgRole, setOrgRole] = useState<string | null>(null);
  const [rows, setRows] = useState<Finding[]>([]);
  const [total, setTotal] = useState(0);
  const [contexts, setContexts] = useState<TrackedContext[]>([]);
  // REV-532: the picker queries the server as you type rather than pre-loading.
  // `contexts` remains the initial page (and the seeded context from the URL);
  // these hold the live query state.
  const [contextQuery, setContextQuery] = useState('');
  const [contextOptions, setContextOptions] = useState<TrackedContext[]>([]);
  // REV-948: the term `contextOptions` answers, or null before any lookup has
  // come back. Without it an empty answer was indistinguishable from "not
  // asked yet", so a search that matched nothing fell back to the initial page
  // and announced those places as matches.
  const [contextOptionsTerm, setContextOptionsTerm] = useState<string | null>(null);
  const [contextSearching, setContextSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // REV-512: seed the filters from the URL so the entry path's intent survives.
  // Lazy initialisers — this is a starting state, not a subscription: once the
  // user touches a control the URL is no longer the source of truth.
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>(() =>
    paramFromAllowed(searchParams, 'source', SOURCES)
  );
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>(() =>
    paramFromAllowed(searchParams, 'severity', SEVERITIES)
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    () => paramFromAllowed(searchParams, 'status', STATUS_VALUES) as StatusFilter
  );
  // The context the URL asked for, if any. Derived from searchParams (stable
  // per navigation) rather than from the live filter state, so the effect that
  // guarantees it appears in the dropdown does not re-fire — and re-fetch every
  // context and facet — each time the user changes a filter.
  const seededContextId = useMemo(() => {
    const raw = searchParams.get('context_id');
    return raw && UUID_RE.test(raw) ? raw : null;
  }, [searchParams]);

  const [contextIdFilter, setContextIdFilter] = useState<string>(() => seededContextId ?? ALL);
  // WCAG SC and category are open sets (they come from per-org facets), so they
  // cannot be validated here. A value with no matches simply returns an empty
  // list rather than a 400.
  const [wcagFilter, setWcagFilter] = useState<string>(() => searchParams.get('wcag_sc_id') || ALL);
  const [categoryFilter, setCategoryFilter] = useState<string>(
    () => searchParams.get('category') || ALL
  );
  // REV-548: closed set, so an unknown value falls back to ALL rather than
  // being forwarded — the API 400s on an out-of-range level, which would blank
  // the whole list instead of just ignoring a bad link.
  const [wcagLevelFilter, setWcagLevelFilter] = useState<WcagLevel | typeof ALL>(() =>
    paramFromAllowed(searchParams, 'wcag_level', WCAG_LEVELS)
  );
  // Open set (any rule id the org has findings for), so it cannot be validated
  // here. Arrives from a report's Top Rules drill-through rather than being
  // picked from scratch, so it is surfaced as a clearable chip instead of an
  // empty text box nobody could usefully fill in.
  const [ruleIdFilter, setRuleIdFilter] = useState<string>(
    () => searchParams.get('rule_id') || ALL
  );
  // REV-1097: the grouped view, and the one group whose findings are open, are
  // read from the URL on every render rather than seeded once like the
  // filters: they are the state a shared link or a reload has to bring back,
  // and a group's link is how its findings are reached.
  const grouped = searchParams.get('view') === 'grouped';
  const signatureParam = searchParams.get('signature');
  const signature = signatureParam && SIGNATURE_RE.test(signatureParam) ? signatureParam : null;
  const [groupRows, setGroupRows] = useState<GroupedOrgFinding[]>([]);
  const [facets, setFacets] = useState<OrgFindingsFacets>({ categories: [], wcag_scs: [] });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  // REV-1097: a group's link, and Back, change the view in the URL without
  // going through a handler that resets the page, and the page index of one
  // view means nothing in another (page 2 of the groups opened a group's
  // findings at an empty page 2). Reset during render, not in an effect, so
  // the list is never fetched at the old offset first.
  const viewKey = `${grouped ? 'grouped' : 'page'} ${signature ?? ''}`;
  const [pagedView, setPagedView] = useState(viewKey);
  if (pagedView !== viewKey) {
    setPagedView(viewKey);
    setPage(0);
  }

  const filters: OrgFindingsFilters = useMemo(
    () => ({
      // `ALL` is a display-only sentinel — it must never reach the API.
      source: sourceFilter === ALL ? undefined : sourceFilter,
      severity: severityFilter === ALL ? undefined : severityFilter,
      status: statusFilter === ALL ? undefined : statusFilter,
      context_id: contextIdFilter === ALL ? undefined : contextIdFilter,
      wcag_sc_id: wcagFilter === ALL ? undefined : wcagFilter,
      category: categoryFilter === ALL ? undefined : categoryFilter,
      wcag_level: wcagLevelFilter === ALL ? undefined : wcagLevelFilter,
      rule_id: ruleIdFilter === ALL ? undefined : ruleIdFilter,
      signature: signature ?? undefined,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }),
    [
      sourceFilter,
      severityFilter,
      statusFilter,
      contextIdFilter,
      wcagFilter,
      categoryFilter,
      wcagLevelFilter,
      ruleIdFilter,
      signature,
      page,
      rowsPerPage,
    ]
  );

  // REV-816: export the findings list as a CSV, honouring the filters on
  // screen. The button stays enabled (never pre-disable — the empty-list case
  // returns early before it renders), and is disabled only while the request is
  // in flight. A failure surfaces as an alert rather than a silent no-op.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // The waiting state and the completion are status messages (WCAG 2.2 SC
  // 4.1.3, AA). The button's own label change to "Exporting…" cannot carry
  // them: pressing it sets the native `disabled`, which drops focus to the
  // body, so a screen reader never hears the new name. This string is rendered
  // into a polite live region instead, which announces wherever focus is. A
  // failure is left to the error Alert's role="alert".
  const [exportStatus, setExportStatus] = useState('');

  const handleExportCsv = useCallback(async () => {
    if (!orgId) return;
    setExporting(true);
    setExportError(null);
    setExportStatus(t('findings.export.announceStart', 'Exporting findings'));
    try {
      const blob = await findingsService.exportCsv(orgId, filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = findingsService.exportFilename(orgId, filters.context_id);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExportStatus(t('findings.export.announceDone', 'Findings exported'));
    } catch (err) {
      // The request asked for a Blob, so the error body arrived as one too.
      // Parse it back into the envelope toDisplayError reads, or a 429 and a
      // 500 both come out as the same sentence.
      await parseBlobErrorBody(err);
      setExportError(
        toDisplayError(err, t('findings.export.error', 'Could not export the findings.'))
      );
      setExportStatus('');
    } finally {
      setExporting(false);
    }
  }, [orgId, filters, t]);

  // Load the context list once per org — it's only used to populate the
  // context filter dropdown and shouldn't re-fire on every filter change.
  useEffect(() => {
    if (!effectiveSlug) return;
    let cancelled = false;
    void (async () => {
      try {
        const org = await organizationService.getBySlug(effectiveSlug);
        if (cancelled) return;
        setOrgId(org.id);
        setOrgRole(org.role ?? null);
        // REV-489: each dropdown's options are fetched independently. These used
        // to share one unguarded Promise.all, so the contexts request failing
        // (it always did — see CONTEXT_FILTER_LIMIT) also discarded the facets,
        // emptying the WCAG SC and Content type filters along with Tracked place.
        const [list, orgFacets] = await Promise.all([
          contextService
            .list(org.id, { limit: CONTEXT_FILTER_LIMIT })
            .catch(() => ({ items: [] as TrackedContext[] })),
          findingsService.getFacets(org.id).catch(() => ({ categories: [], wcag_scs: [] })),
        ]);
        if (cancelled) return;
        setFacets(orgFacets);

        // REV-512: a deep-linked context may not be in the fetched page — the
        // list is capped at CONTEXT_FILTER_LIMIT, so anything beyond the first
        // 100 is missing. MUI then renders the Select blank ("out-of-range
        // value"), which is exactly the looks-broken symptom REV-489 fixed:
        // the filter is applied but the control shows nothing. Fetch the one
        // missing context and add it so the scope is always visible.
        let items = list.items;
        if (seededContextId && !items.some((c) => c.id === seededContextId)) {
          const scoped = await contextService
            .getById(org.id, seededContextId)
            .catch(() => null as TrackedContext | null);
          if (cancelled) return;
          if (scoped) items = [scoped, ...items];
        }
        setContexts(items);
      } catch {
        // Swallow: the filter dropdown is a secondary affordance and the
        // findings load below surfaces the real error.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveSlug, seededContextId]);

  /**
   * REV-705: is the empty table empty because of the FILTERS, or because there
   * is nothing to show?
   *
   * The distinction is the whole point. Telling someone who has never run a
   * scan to "widen the filters" is advice they cannot act on, and it implies
   * their results are being hidden rather than absent.
   */
  const anyFilterActive =
    sourceFilter !== ALL ||
    severityFilter !== ALL ||
    statusFilter !== ALL ||
    contextIdFilter !== ALL ||
    wcagFilter !== ALL ||
    categoryFilter !== ALL ||
    wcagLevelFilter !== ALL ||
    ruleIdFilter !== ALL ||
    signature !== null;

  /**
   * REV-705: the findings view used to render its whole apparatus — eight
   * filter dropdowns, the trend panel, a full table header — around a single
   * line of grey text, for a user who has never run a scan. That is a lot of
   * controls implying there is something to control.
   *
   * When there is nothing at all AND no filter is narrowing anything, replace
   * the apparatus with one panel that says so and offers the next step, the
   * way the dashboard's no-organization state does (see HomePage). The
   * FILTERED empty case deliberately keeps the apparatus: the filters are
   * exactly what that user needs to reach in order to widen them.
   */
  const showNothingYet =
    !loading && !error && (grouped ? groupRows : rows).length === 0 && !anyFilterActive;

  // REV-1097: the number of the latest load. Loads overlap (a view toggle or a
  // filter change while one is in flight), and only the latest may write the
  // list: an older per-page answer landing after the groups emptied the
  // grouped table, and with no filter set the page then said no findings
  // existed at all.
  const latestLoad = useRef(0);
  const load = useCallback(async () => {
    const thisLoad = ++latestLoad.current;
    const isLatest = () => thisLoad === latestLoad.current;
    if (!orgId) {
      // REV-589: no organization chosen in the header (and none in the URL),
      // so there is nothing to scope a query to. Clear the initial spinner
      // rather than leaving the page turning forever — the empty state says
      // more than a spinner that never resolves. The org-list effect used to
      // do this when the user had no orgs at all; that effect is gone with the
      // page's own picker.
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (grouped) {
        const res = await findingsService.listGroupsForOrg(orgId, filters);
        if (!isLatest()) return;
        setGroupRows(res.items ?? []);
        setRows([]);
        setTotal(res.pagination.total);
      } else {
        const res = await findingsService.listForOrg(orgId, filters);
        if (!isLatest()) return;
        setRows(res.items ?? []);
        setGroupRows([]);
        setTotal(res.pagination.total);
      }
      // REV-720: the selection is pruned by useBulkTriage when the rows it
      // covers stop being on screen, rather than cleared from here — the hook
      // is defined after this callback and owns the rule either way.
    } catch (err) {
      if (isLatest()) {
        setError(toDisplayError(err, t('findings.errors.fetchFailed', 'Failed to load findings')));
      }
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [orgId, filters, grouped, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetPage = useCallback(() => setPage(0), []);

  const handleSource = (e: SelectChangeEvent) => {
    setSourceFilter(e.target.value as SourceFilter);
    resetPage();
  };
  const handleSeverity = (e: SelectChangeEvent) => {
    setSeverityFilter(e.target.value as SeverityFilter);
    resetPage();
  };
  const handleStatus = (e: SelectChangeEvent) => {
    setStatusFilter(e.target.value as StatusFilter);
    resetPage();
  };
  const handleWcagLevel = (e: SelectChangeEvent) => {
    setWcagLevelFilter(e.target.value as WcagLevel | typeof ALL);
    resetPage();
  };
  const clearRuleFilter = () => {
    setRuleIdFilter(ALL);
    resetPage();
  };
  /**
   * REV-1097: switch between one row per page and one row per issue. Written
   * to the URL (replacing the entry, not adding one), so the view survives a
   * reload and a shared link. Choosing a view leaves any one group's findings.
   */
  const setView = (view: 'page' | 'grouped') => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (view === 'grouped') next.set('view', 'grouped');
        else next.delete('view');
        next.delete('signature');
        return next;
      },
      { replace: true }
    );
    resetPage();
  };
  const clearSignature = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('signature');
        return next;
      },
      { replace: true }
    );
    resetPage();
  };
  /** Where a group's own per-page findings are listed: this page, by signature. */
  const groupHref = (g: GroupedOrgFinding) => {
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    next.set('signature', g.signature);
    return `${location.pathname}?${next.toString()}`;
  };
  /**
   * REV-532: query the contexts endpoint as the user types. The endpoint caps
   * limit at 100, so a pre-loaded list silently truncated for any org with more
   * than 100 tracked places — and large consulting orgs are exactly the ones
   * that exceed it.
   *
   * Debounced so a keystroke is not a request. The effect ignores its own
   * result if a newer query has started (`stale`), otherwise a slow response
   * for "mar" can land after "market" and repopulate the list with the wrong
   * options.
   */
  useEffect(() => {
    if (!orgId) return;
    const term = contextQuery.trim();
    let stale = false;
    setContextSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const res = await contextService.list(orgId, {
          limit: CONTEXT_FILTER_LIMIT,
          ...(term ? { search: term } : {}),
        });
        if (!stale) {
          setContextOptions(res.items ?? []);
          setContextOptionsTerm(term);
        }
      } catch {
        // A failed lookup must not blank the picker: keep whatever is showing
        // and let the user retry by typing.
        if (!stale) setContextOptions((prev) => prev);
      } finally {
        if (!stale) setContextSearching(false);
      }
    }, 250);
    return () => {
      stale = true;
      window.clearTimeout(timer);
    };
  }, [orgId, contextQuery]);

  /**
   * REV-948: the last search the server answered matched no place. The picker
   * then offers nothing and says so, rather than showing places that did not
   * match. An empty answer to the unfiltered list (an org with no places) is
   * not a search and keeps its "0 tracked places available" announcement.
   */
  const contextSearchMatchedNothing = !!contextOptionsTerm && contextOptions.length === 0;

  /**
   * The sentinel "All places" row plus the server's current results, and always
   * the selected context itself — otherwise selecting a place and then typing a
   * query that excludes it would leave Autocomplete with a value not present in
   * `options`, which MUI warns about and which blanks the input.
   */
  const contextPickerOptions = useMemo(() => {
    const base = contextOptions.length > 0 ? contextOptions : contexts;
    const selected = base.find((c) => c.id === contextIdFilter);
    const missingSelected =
      contextIdFilter !== ALL && !selected
        ? contexts.find((c) => c.id === contextIdFilter)
        : undefined;
    // Built inside the memo, not above it: a sentinel constructed on every
    // render is a new object each time, so it can never be a stable dependency
    // — which is what the exhaustive-deps rule was objecting to. Suppressing
    // the rule would have hidden that rather than fixed it.
    const allOption = {
      id: ALL,
      name: t('findings.filters.allContexts', 'All places'),
    } as TrackedContext;
    return [allOption, ...(missingSelected ? [missingSelected] : []), ...base];
  }, [contextOptions, contexts, contextIdFilter, t]);

  const selectedContext =
    contextPickerOptions.find((c) => c.id === contextIdFilter) ?? contextPickerOptions[0];
  const selectedContextId = selectedContext?.id ?? ALL;
  const selectedContextName = selectedContext?.name ?? '';
  /**
   * REV-948: one object per selection, not per option list. Every search
   * answer rebuilds `contextPickerOptions`, and MUI treats a new `value` object
   * as a new selection: it overwrote the text being typed with "All places"
   * each time results arrived. `isOptionEqualToValue` matches on id, so the
   * value need not be the option object itself.
   */
  const selectedContextOption = useMemo(
    () => ({ id: selectedContextId, name: selectedContextName }) as TrackedContext,
    [selectedContextId, selectedContextName]
  );

  const handleContext = (value: string) => {
    setContextIdFilter(value);
    resetPage();
  };
  const handleWcag = (e: SelectChangeEvent) => {
    setWcagFilter(e.target.value);
    resetPage();
  };
  const handleCategory = (e: SelectChangeEvent) => {
    setCategoryFilter(e.target.value);
    resetPage();
  };
  // REV-720: bulk triage moved to a hook so the scan-run view can offer the
  // same actions. This page owns only where its errors are shown.
  const bulk = useBulkTriage({
    organizationId: orgId,
    // REV-949: a viewer can read findings but not change their status.
    canTriage: canTriageFindings(orgRole),
    rows,
    onChanged: load,
    onError: setError,
  });

  // Destination for a finding row. Auto findings → REV-134 detail page (WCAG
  // ref, code samples, remediation, feedback actions); manual findings → their
  // audit review page. Finding.id is namespaced as "auto-<uuid>" /
  // "manual-<uuid>" by the list endpoint so the two sources share one key
  // space — the detail route takes the bare uuid, hence the prefix strip
  // fallback. Returns undefined when there is no navigable target.
  const findingHref = (f: Finding): string | undefined => {
    if (!effectiveSlug) return undefined;
    if (f.source === 'auto') {
      const bareId = f.raw_id || f.id.replace(/^auto-/, '');
      return `/orgs/${effectiveSlug}/findings/${bareId}`;
    }
    if (f.source === 'manual' && f.audit_session_id) {
      return `/orgs/${effectiveSlug}/audits/${f.audit_session_id}`;
    }
    return undefined;
  };

  const header = (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'flex-start' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
            {t('findings.title', 'Findings')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t(
              'findings.subtitle',
              'Every open accessibility issue across every tracked place in this organization.'
            )}
          </Typography>
        </Box>
        {/* REV-816: exports the findings matching the filters on screen. A
            download is an action on the page, not a navigation, so it is a
            button (never a link). Disabled only while the request is in flight
            — never pre-disabled. Hidden until an org is resolved, since the
            export is org-scoped.
            `header` is rendered by the showNothingYet branch too, so the
            control is also gated on it: an org with no findings at all has
            nothing to export, and REV-705 replaced that panel's apparatus
            precisely so it would stop implying there was something to control.
            The FILTERED empty case deliberately keeps the button, for the same
            reason it keeps the filters. */}
        {orgId && !showNothingYet && (
          <Button
            variant="outlined"
            startIcon={
              exporting ? (
                <CircularProgress size={16} aria-hidden="true" />
              ) : (
                <FileDownloadOutlined />
              )
            }
            onClick={() => void handleExportCsv()}
            disabled={exporting}
          >
            {exporting
              ? t('findings.export.inProgress', 'Exporting…')
              : t('findings.export.csv', 'Export CSV')}
          </Button>
        )}
      </Stack>
    </>
  );

  // REV-705: one panel in place of the apparatus. Returning early rather than
  // wrapping the rest keeps the main tree at its own indentation — the diff
  // stays about the change instead of about whitespace.
  //
  // Two audiences land here and they need different next steps: a user with no
  // organization cannot "run a test on a tracked place", and pointing them at
  // one would be the same unactionable advice this ticket is about.
  if (showNothingYet) {
    return (
      <Box sx={{ p: 3 }}>
        {header}
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {orgId
              ? t('findings.nothingYet.title', 'No findings yet')
              : t('findings.noOrg.title', 'Choose an organization')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {orgId
              ? t(
                  'findings.nothingYet.body',
                  'Run a test on one of your tracked places and its results will appear here.'
                )
              : t(
                  'findings.noOrg.body',
                  'Pick an organization from the switcher in the header — or create one if you do not have any yet.'
                )}
          </Typography>
          {/* REV-732: both arms are destinations, so this is a link either way.
              The label changes with the org, and so does the href — they are
              derived from the same condition so they cannot disagree, which is
              the defect REV-705 was about in the first place. */}
          <Button
            variant="contained"
            component={RouterLink}
            to={orgId ? `/tests/new?orgId=${orgId}` : '/orgs'}
          >
            {orgId
              ? t('findings.nothingYet.cta', 'Run a test')
              : t('findings.noOrg.cta', 'Create an organization')}
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {header}

      {/* Rendered unconditionally and empty until there is something to say: a
          live region has to be in the DOM before its text changes, or inserting
          the region and its content together may not be announced at all. */}
      <Box aria-live="polite" data-testid="export-status" sx={visuallyHidden}>
        {exportStatus}
      </Box>

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          useFlexGap
          flexWrap="wrap"
        >
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="findings-source-label">
              {t('findings.filters.source', 'Source')}
            </InputLabel>
            <Select
              labelId="findings-source-label"
              id="findings-source"
              value={sourceFilter}
              label={t('findings.filters.source', 'Source')}
              onChange={handleSource}
            >
              <MenuItem value={ALL}>{t('findings.filters.allSources', 'All sources')}</MenuItem>
              <MenuItem value="auto">{t('findings.sources.auto', 'Automated')}</MenuItem>
              <MenuItem value="manual">{t('findings.sources.manual', 'Manual')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="findings-severity-label">
              {t('findings.filters.severity', 'Severity')}
            </InputLabel>
            <Select
              labelId="findings-severity-label"
              id="findings-severity"
              value={severityFilter}
              label={t('findings.filters.severity', 'Severity')}
              onChange={handleSeverity}
            >
              <MenuItem value={ALL}>
                {t('findings.filters.allSeverities', 'All severities')}
              </MenuItem>
              {SEVERITIES.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(`findings.severities.${s}`, s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="findings-status-label">
              {t('findings.filters.status', 'Status')}
            </InputLabel>
            <Select
              labelId="findings-status-label"
              id="findings-status"
              value={statusFilter}
              label={t('findings.filters.status', 'Status')}
              onChange={handleStatus}
            >
              <MenuItem value={ALL}>{t('findings.filters.allStatuses', 'All open')}</MenuItem>
              {/* REV-548: the set the issues-breakdown report counts. Offered
                  as one value so a report drill-through lands on exactly the
                  number the row displayed. */}
              <MenuItem value={UNRESOLVED}>
                {t('findings.filters.unresolved', 'Unresolved (excl. dismissed)')}
              </MenuItem>
              <MenuItem value={ALL_STATUSES}>
                {t('findings.filters.allStatusesIncl', 'All statuses (incl. resolved)')}
              </MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(`findings.statuses.${s}`, s.replace('_', ' '))}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* REV-548: closed set of four, so a plain Select is right. The
              drill-through from the report's "By WCAG level" table lands here
              with one already chosen, and the user can see and change it. */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="findings-wcag-level-label">
              {t('findings.filters.wcagLevel', 'WCAG level')}
            </InputLabel>
            <Select
              labelId="findings-wcag-level-label"
              id="findings-wcag-level"
              value={wcagLevelFilter}
              label={t('findings.filters.wcagLevel', 'WCAG level')}
              onChange={handleWcagLevel}
            >
              <MenuItem value={ALL}>{t('findings.filters.allWcagLevels', 'All levels')}</MenuItem>
              {WCAG_LEVELS.map((lvl) => (
                <MenuItem key={lvl} value={lvl}>
                  {lvl === 'unspecified'
                    ? t('findings.filters.wcagLevelUnspecified', 'Unspecified')
                    : lvl}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* REV-548: rule ids are an open set, so there is nothing useful to
              pre-populate a control with. It only ever arrives from the
              report's "Top Rules" drill-through — so show it when active and
              make it clearable, rather than adding an empty text box. */}
          {ruleIdFilter !== ALL && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* A plain Chip plus a separate button, NOT Chip's onDelete with
                  a nested IconButton: Chip's onDelete makes the chip itself
                  focusable, so a button inside it nests one interactive control
                  in another. */}
              <Chip
                label={t('findings.filters.ruleChip', 'Rule: {{rule}}', { rule: ruleIdFilter })}
                size="small"
              />
              <IconButton
                size="small"
                onClick={clearRuleFilter}
                aria-label={t('findings.filters.clearRule', 'Clear rule filter')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {/* REV-532: a plain Select cannot scale past the endpoint's 100-row
              cap, so an org with more than 100 tracked places saw a silently
              truncated list. This queries the server as the user types.

              Accessibility: Autocomplete renders a real combobox with
              aria-expanded/aria-controls and full keyboard support. The
              TextField supplies the accessible name, and the option count is
              announced via a live region below — a combobox that filters
              silently leaves screen-reader users with no idea whether their
              typing matched anything. */}
          <Autocomplete
            // id belongs on Autocomplete, NOT on the inner TextField. MUI
            // derives the input id and the label's htmlFor from it together;
            // setting it on the TextField instead renamed the input while the
            // label kept pointing at the old id, leaving a dangling `for`
            // (STRUCTURE-12) — caught by the dogfood gate, not by unit tests.
            id="findings-context"
            size="small"
            sx={{ minWidth: 220 }}
            options={contextPickerOptions}
            value={selectedContextOption}
            loading={contextSearching}
            onInputChange={(_e, value, reason) => {
              // 'reset' fires when a value is selected; re-querying then would
              // throw away the option list the user just picked from.
              if (reason === 'input') setContextQuery(value);
              // REV-948: except after a search that matched nothing. Leaving
              // the field ('blur') or clearing it puts something other than the
              // term back in it, and reopening must not still say "no places
              // match" a term that is no longer there.
              else if (contextSearchMatchedNothing) setContextQuery('');
            }}
            onChange={(_e, option) => handleContext(option ? option.id : ALL)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            // Server-side search already filtered; filtering again client-side
            // would hide rows the server deliberately returned.
            //
            // REV-948: when the search matched nothing, offer nothing — not the
            // "All places" row, and not the selected place (kept in `options`
            // only so MUI can resolve the value). An empty list is what makes
            // MUI show `noOptionsText` instead of a list that looks like hits.
            filterOptions={(x) => (contextSearchMatchedNothing ? [] : x)}
            noOptionsText={t('findings.filters.contextNoMatch', {
              query: contextOptionsTerm ?? '',
            })}
            renderInput={(params) => (
              <TextField {...params} label={t('findings.filters.context', 'Tracked place')} />
            )}
          />
          <Box aria-live="polite" sx={visuallyHidden}>
            {contextSearching
              ? t('findings.filters.contextSearching', 'Searching tracked places')
              : contextSearchMatchedNothing
                ? // REV-948: say the search matched nothing. A count here would
                  // be a count of places the search did not match.
                  t('findings.filters.contextNoMatch', { query: contextOptionsTerm })
                : t('findings.filters.contextResults', {
                    // REV-939: the options include the "All places" sentinel,
                    // which is not a place. Counting it told an org with one
                    // tracked place that two were available.
                    count: contextPickerOptions.filter((c) => c.id !== ALL).length,
                  })}
          </Box>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="findings-wcag-label">
              {t('findings.filters.wcag', 'WCAG SC')}
            </InputLabel>
            <Select
              labelId="findings-wcag-label"
              id="findings-wcag"
              value={wcagFilter}
              label={t('findings.filters.wcag', 'WCAG SC')}
              onChange={handleWcag}
            >
              <MenuItem value={ALL}>{t('findings.filters.allWcag', 'All SC')}</MenuItem>
              {facets.wcag_scs.map((sc) => (
                <MenuItem key={sc} value={sc}>
                  {sc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="findings-category-label">
              {t('findings.filters.category', 'Content type')}
            </InputLabel>
            <Select
              labelId="findings-category-label"
              id="findings-category"
              value={categoryFilter}
              label={t('findings.filters.category', 'Content type')}
              onChange={handleCategory}
            >
              <MenuItem value={ALL}>
                {t('findings.filters.allCategories', 'All content types')}
              </MenuItem>
              {facets.categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <FindingsTrendPanel
        orgId={orgId}
        contextId={contextIdFilter === ALL ? undefined : contextIdFilter}
        severity={(severityFilter === ALL ? undefined : severityFilter) as Severity | undefined}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <BulkTriageBar bulk={bulk} />

      <Typography
        id="findings-list-heading"
        variant="h6"
        component="h2"
        gutterBottom
        sx={{ mt: 1 }}
      >
        {/* REV-1097: the heading names whichever table is below it, so the
            grouped table is not headed "Itemized findings". */}
        {grouped
          ? t('findings.grouped.tableLabel', 'Findings grouped across pages')
          : t('findings.listHeading', 'Itemized findings')}
      </Typography>
      {/* REV-1097: one row per page, or one per issue across pages. Buttons,
          not links: they change the view in place (the URL records it, but
          with replace, not as a new destination). */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={grouped ? 'grouped' : 'page'}
          onChange={(_e, view: 'page' | 'grouped' | null) => {
            if (view) setView(view);
          }}
          aria-label={t('findings.grouped.toggleLabel', 'Show findings')}
        >
          <ToggleButton value="page">
            {t('findings.grouped.byPage', 'One row per page')}
          </ToggleButton>
          <ToggleButton value="grouped">
            {t('findings.grouped.byIssue', 'Grouped across pages')}
          </ToggleButton>
        </ToggleButtonGroup>
        {signature && (
          // Same shape as the rule chip above: a plain Chip and a separate
          // button, never a button nested in a focusable chip.
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              size="small"
              label={t(
                'findings.grouped.signatureChip',
                'Showing the findings of one grouped issue'
              )}
            />
            <IconButton
              size="small"
              onClick={clearSignature}
              aria-label={t('findings.grouped.clearSignature', 'Show all findings')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Stack>
      {grouped ? (
        <GroupedFindingsTable
          rows={groupRows}
          loading={loading}
          labelledBy="findings-list-heading"
          regionLabel={t('findings.grouped.tableLabel', 'Findings grouped across pages')}
          outlined
          hrefFor={groupHref}
          empty={
            error
              ? null
              : anyFilterActive
                ? t(
                    'findings.emptyFiltered',
                    'No findings match these filters. Widen them to see more.'
                  )
                : t(
                    'findings.emptyNoScans',
                    'No findings yet. Run a test on a tracked place and its results will appear here.'
                  )
          }
          footer={
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                resetPage();
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          }
        />
      ) : (
        <FindingsTable
          rows={rows}
          loading={loading}
          labelledBy="findings-list-heading"
          regionLabel={t('findings.tableLabel', 'Findings')}
          outlined
          hrefFor={findingHref}
          selection={bulk.selection}
          empty={
            error
              ? null
              : /* REV-705: two different situations wore the same message. "No
                 findings match these filters... widen the filters" is unhelpful
                 advice for someone who has never run a scan and has no filters
                 set — there is nothing to widen, and it reads as though results
                 were hidden rather than absent. Tell each of them the true
                 thing. */
                anyFilterActive
                ? t(
                    'findings.emptyFiltered',
                    'No findings match these filters. Widen them to see more.'
                  )
                : t(
                    'findings.emptyNoScans',
                    'No findings yet. Run a test on a tracked place and its results will appear here.'
                  )
          }
          footer={
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                resetPage();
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          }
        />
      )}

      {orgId === null && !loading && !error && (
        // Guard for the case where the org resolved but orgId state is
        // still pending — should almost never hit in practice, surfaced
        // here so tests can exercise a deterministic ready state.
        <Box sx={{ display: 'none' }} aria-hidden="true" />
      )}
    </Box>
  );
}
